/**
 * プライバシー保護の設定 `/privacy`。
 *
 * Webメールの画像プロキシまわりの運営設定を 1 枚にまとめる。
 * `apps/api/src/routes/ops/privacy.ts` の 8 本には**画面が 1 つも無く**、
 * 出口 IP を足すのもトラッカーを登録するのも psql を叩くしかなかった
 * （どちらも「入れた瞬間に全契約者の見え方が変わる」設定なので、
 * 監査の残る API があるのに使われない状態がいちばん悪い）。
 *
 * ## 3 つのタブが独立している理由
 *
 *   - **出口 IP**（`proxy_egress_ips`）… 画像を取りに行くときの送信元。
 *     登録しても **API ホストの NIC に載っていなければ bind されない**ので、
 *     サーバーが返す `bindable` をそのまま出す（登録できた＝使われる、ではない）。
 *   - **既知トラッカー**（`tracker_host_rules`）… ここに入れたホストの画像は
 *     取りに行かない。ワイルドカードは API と DB の両方で禁止されている
 *     （1 行で「全部トラッカー」を作れると全契約者の画像が一斉に消えるため）。
 *   - **取得の実績**（`image_proxy_objects` の集計）… 帯域と、新しく現れた
 *     計測ホストを見つけるためのもの。**どの組織が開いたかは返らない**。
 */
import * as React from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, EyeOff, Network, Plus, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Input,
  PageHeader,
  Section,
  StatCard,
  Textarea,
  formatBytes,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { ImageProxyHostRow, ProxyEgressIp, TrackerHostRule } from '../types/api.js';

const routeApi = getRouteApi(RID.privacy);

export function PrivacyPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canRead = usePermission(OPS_PERMISSION.securityRead);

  if (!canRead) return <PermissionDenied />;

  return (
    <>
      <PageHeader
        title="プライバシー保護"
        description="Webメールの画像プロキシが使う出口 IP、取得を止める既知トラッカー、そして実際の取得実績。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'egress', label: '出口 IP', icon: Network },
          { value: 'trackers', label: '既知トラッカー', icon: EyeOff },
          { value: 'usage', label: '取得の実績', icon: Activity },
        ]}
      />

      {search.tab === 'egress' ? <EgressTab /> : null}
      {search.tab === 'trackers' ? <TrackersTab /> : null}
      {search.tab === 'usage' ? <UsageTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------- 出口 IP */

function EgressTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<ProxyEgressIp | null>(null);

  const query = useQuery({
    queryKey: qk.proxyEgressIps(),
    queryFn: () => api.get<{ egressIps: ProxyEgressIp[] }>('/privacy/egress-ips'),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: qk.proxyEgressIps() });
  };

  const toggle = useMutation({
    // `.strict()` なので `enabled` だけを送る。有効へ戻すとき、サーバー側が
    // 失敗カウントも消す（消さないと次の 1 回でまた無効化される）。
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api.patch(`/privacy/egress-ips/${v.id}`, { enabled: v.enabled }),
    onSuccess: invalidate,
  });

  const rows = query.data?.egressIps ?? [];
  const unbindable = rows.filter((r) => !r.bindable);
  const autoDisabled = rows.filter((r) => !r.enabled && r.disabledReason !== null);

  const columns: Array<DataTableColumn<ProxyEgressIp>> = [
    {
      id: 'address',
      header: 'アドレス',
      cell: (r) => (
        <div className="min-w-0">
          <span className="block truncate font-mono text-xs">{r.address}</span>
          {r.label ? <span className="block truncate text-xs text-muted-foreground">{r.label}</span> : null}
        </div>
      ),
    },
    {
      id: 'state',
      header: '状態',
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          <Badge variant={r.enabled ? 'success' : 'muted'} size="sm">
            {r.enabled ? '有効' : '無効'}
          </Badge>
          {/*
            **「登録できた」と「使われる」は別。** この API ホストの NIC に
            載っていない IP は bind されず、送信元にならない。台数を増やした
            ときに片方のホストにしか載っていない IP がここで見える。
          */}
          {r.bindable ? null : (
            <Badge variant="warning" size="sm">
              NIC に無い
            </Badge>
          )}
        </div>
      ),
    },
    {
      id: 'failures',
      header: '失敗',
      align: 'right',
      cell: (r) => (r.failureCount > 0 ? formatCount(r.failureCount) : '—'),
    },
    {
      id: 'lastUsed',
      header: '最終使用',
      hideBelow: 'md',
      cell: (r) => (r.lastUsedAt ? formatRelative(r.lastUsedAt) : '未使用'),
    },
    {
      id: 'reason',
      header: '無効化の理由',
      hideBelow: 'lg',
      cell: (r) =>
        r.disabledReason ? (
          <span className="text-xs text-warning">
            {r.disabledReason}
            {r.lastError ? `（${r.lastError}）` : ''}
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-40',
      cell: (r) =>
        canWrite ? (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggle.mutate({ id: r.id, enabled: !r.enabled })}
              loading={toggle.isPending}
            >
              {r.enabled ? '無効にする' : '有効に戻す'}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} aria-label="削除">
              <Trash2 className="size-4 text-destructive" aria-hidden />
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canWrite ? (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus aria-hidden />
            出口 IP を追加
          </Button>
        ) : null}
      </div>

      {rows.length === 0 && !query.isLoading ? (
        /*
         * **プランの機能表との食い違いをここで見せる。**
         * `standard` 以上は `imageProxyIpRotation: true` を謳っており、
         * 契約者面（`GET /portal/privacy`）も features に true を返す。
         * ところが `proxy_egress_ips` が 0 行なら `pickEgressIp()` は null を
         * 返し、取得はホスト既定の出口から素通しになる（失敗にはならない）。
         * つまり**約束はしているのに 1 つも回っていない**状態で、
         * 運営がそれに気付く手掛かりがこの画面以外に無い。
         */
        <Alert variant="warning" className="mb-4">
          <AlertTitle>出口 IP が 0 件です。IP ローテーションは働いていません</AlertTitle>
          <AlertDescription>
            standard 以上のプランは「画像プロキシの IP ローテーション」を機能として掲げていますが、
            ここが空だと全受信者ぶんの取得が同じ 1 本（このホストの既定アドレス）から出ます。
            画像プロキシ自体は動くので、<strong>失敗としては表面化しません</strong>。
            専用の IP を数本登録すると、送信用 IP のレピュテーションを画像取得の挙動から切り離せます。
          </AlertDescription>
        </Alert>
      ) : null}

      {unbindable.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertTitle>{formatCount(unbindable.length)} 件がこのホストの NIC に載っていません</AlertTitle>
          <AlertDescription>
            登録はされていますが bind できないため、その IP から出て行くことはありません。
            API を複数台で動かしている場合は、全台に同じアドレスを載せてください。
          </AlertDescription>
        </Alert>
      ) : null}

      {autoDisabled.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertTitle>{formatCount(autoDisabled.length)} 件が失敗により自動で無効化されています</AlertTitle>
          <AlertDescription>
            「有効に戻す」を押すと失敗カウントも一緒に消えます（消さないと次の 1 回でまた無効化されます）。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="出口 IP" />}
      />

      <AddEgressDialog open={addOpen} onOpenChange={setAddOpen} onCreated={invalidate} />
      <DeleteEgressDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={invalidate} />
    </>
  );
}

function AddEgressDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): React.ReactElement {
  const [address, setAddress] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setAddress('');
      setLabel('');
      setNote('');
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/privacy/egress-ips', {
        address: address.trim(),
        label: label.trim(),
        note: note.trim(),
      }),
    onSuccess: () => {
      onCreated();
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="出口 IP を追加する"
      description="画像プロキシがリモートの画像を取りに行くときの送信元アドレスです。"
      submitLabel="追加する"
      pending={create.isPending}
      error={create.error}
      disabled={address.trim().length < 3}
      onSubmit={() => create.mutate()}
    >
      <Field
        label="IP アドレス"
        required
        hint="単一のアドレスだけ（CIDR は不可）。IPv4 / IPv6 のどちらでも構いません。"
      >
        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="203.0.113.24" />
      </Field>
      <Field label="ラベル" hint="どの回線・どのホストのアドレスかを書いておくと、後から剥がすときに迷いません。">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="api-01 の画像取得用" />
      </Field>
      <Field label="メモ">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

function DeleteEgressDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: ProxyEgressIp | null;
  onClose: () => void;
  onDeleted: () => void;
}): React.ReactElement {
  const remove = useMutation({
    mutationFn: () => api.del(`/privacy/egress-ips/${target?.id ?? ''}`),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="出口 IP を削除する"
      description="取得の台帳（どの IP から取ったか）は残りますが、その紐付けだけが消えます。使わなくなっただけなら「無効にする」で足ります。"
      confirmText={target?.address ?? ''}
      confirmLabel="削除する"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate()}
    />
  );
}

/* ------------------------------------------------------- 既知トラッカー */

function TrackersTab(): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<TrackerHostRule | null>(null);

  const query = useQuery({
    queryKey: qk.trackerHosts(),
    queryFn: () => api.get<{ rules: TrackerHostRule[] }>('/privacy/tracker-hosts'),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: qk.trackerHosts() });
  };

  /*
   * 一時停止 / 再開（`PATCH /ops/privacy/tracker-hosts/:id`）。
   *
   * **誤検知の緩和を「削除」でやらせない**ためにある。削除すると出典（メモ）・
   * 登録日・登録者が一緒に消え、落ち着いてから入れ直しても監査上は別の規則に
   * なる。出口 IP のトグルと同じ作法で、`.strict()` なので `enabled` だけ送る。
   */
  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api.patch(`/privacy/tracker-hosts/${v.id}`, { enabled: v.enabled }),
    onSuccess: invalidate,
  });

  const rules = query.data?.rules ?? [];
  const paused = rules.filter((r) => !r.enabled);

  const columns: Array<DataTableColumn<TrackerHostRule>> = [
    { id: 'pattern', header: 'ホスト', cell: (r) => <span className="font-mono text-xs">{r.pattern}</span> },
    {
      id: 'enabled',
      header: '状態',
      /*
       * 停止中は「規則としては残っているが、いま画像は取りに行く」状態。
       * 読む側（`services/image-proxy.ts`）が `enabled = true` だけを拾うので、
       * false にした瞬間にそのホストは素通しになる。
       */
      cell: (r) =>
        r.enabled ? (
          <Badge variant="destructive" size="sm">
            取得しない
          </Badge>
        ) : (
          <Badge variant="muted" size="sm">
            停止中（いまは取得する）
          </Badge>
        ),
    },
    { id: 'note', header: 'メモ', hideBelow: 'md', cell: (r) => r.note || '—' },
    { id: 'createdAt', header: '登録', hideBelow: 'lg', cell: (r) => formatDateTime(r.createdAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-40',
      cell: (r) =>
        canWrite ? (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggle.mutate({ id: r.id, enabled: !r.enabled })}
              loading={toggle.isPending}
            >
              {r.enabled ? '一時停止' : '再開する'}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} aria-label="削除">
              <Trash2 className="size-4 text-destructive" aria-hidden />
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canWrite ? (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus aria-hidden />
            ホストを追加
          </Button>
        ) : null}
      </div>

      <Alert className="mb-4">
        <AlertTitle>ここに入れたホストの画像は取りに行きません</AlertTitle>
        <AlertDescription>
          開封トラッキング用の 1×1 画像を配っているホストを登録します。
          ワイルドカードや正規表現は使えません（1 行で「全部トラッカー」を作れると、
          全契約者のメールから画像が一斉に消えるため、API と DB の両方で禁止しています）。
          登録できるのはホスト名か登録可能ドメインだけです。
          誤検知で契約者の正当な画像が消えたときは、削除ではなく
          <strong>一時停止</strong>を使ってください（出典と登録日が残ります）。
        </AlertDescription>
      </Alert>

      {/*
        停止中の規則は「登録してあるのに効いていない」状態。放置すると
        「入れたはずのトラッカーが素通ししている」に気付けないので、
        件数を必ず目に入れる。
      */}
      {paused.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <AlertTitle>{formatCount(paused.length)} 件の規則が停止中です</AlertTitle>
          <AlertDescription>
            停止中のホストの画像は<strong>いま取りに行っています</strong>（開封が送信者に伝わります）。
            誤検知の調査が終わったら再開してください。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rules}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="トラッカー規則" />}
      />

      <AddTrackerDialog open={addOpen} onOpenChange={setAddOpen} onCreated={invalidate} />
      <DeleteTrackerDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={invalidate} />
    </>
  );
}

function DeleteTrackerDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: TrackerHostRule | null;
  onClose: () => void;
  onDeleted: () => void;
}): React.ReactElement {
  const remove = useMutation({
    mutationFn: () => api.del(`/privacy/tracker-hosts/${target?.id ?? ''}`),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="トラッカー規則を削除する"
      description="削除すると、そのホストの画像は再び取得されるようになります。メモ（出典）・登録日・登録者も一緒に消えるので、一時的に外したいだけなら「一時停止」を使ってください。"
      confirmText={target?.pattern ?? ''}
      confirmLabel="削除する"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => remove.mutate()}
    />
  );
}

function AddTrackerDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): React.ReactElement {
  const [pattern, setPattern] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setPattern('');
      setNote('');
    }
  }, [open]);

  // サーバー側と同じ形。**保存してから 400 で怒られるのではなく先に止める。**
  const valid = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(pattern.trim());

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/privacy/tracker-hosts', { pattern: pattern.trim(), note: note.trim() }),
    onSuccess: () => {
      onCreated();
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="既知トラッカーを追加する"
      description="このホストが配る画像は取りに行かなくなります。"
      submitLabel="追加する"
      pending={create.isPending}
      error={create.error}
      disabled={!valid}
      onSubmit={() => create.mutate()}
    >
      <Field
        label="ホスト名"
        required
        error={pattern.trim().length > 0 && !valid ? 'ホスト名か登録可能ドメインを小文字で入力してください（* は使えません）。' : undefined}
        hint="例: track.example.com / open.example.net"
      >
        <Input value={pattern} onChange={(e) => setPattern(e.target.value.toLowerCase())} placeholder="track.example.com" />
      </Field>
      <Field label="メモ" hint="出典（どの調査で見つけたか）を書いておくと、外すときの判断がつきます。">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------------ 取得の実績 */

const USAGE_DAYS = [
  { value: '1', label: '1 日' },
  { value: '7', label: '7 日' },
  { value: '30', label: '30 日' },
  { value: '90', label: '90 日' },
];

function UsageTab(): React.ReactElement {
  const [days, setDays] = React.useState('7');

  const query = useQuery({
    queryKey: qk.imageProxyHosts({ days }),
    queryFn: () => api.get<{ days: number; hosts: ImageProxyHostRow[] }>('/privacy/image-proxy/hosts', { days }),
  });

  const hosts = query.data?.hosts ?? [];
  const totals = hosts.reduce(
    (acc, h) => ({ fetches: acc.fetches + h.fetches, bytes: acc.bytes + h.bytes }),
    { fetches: 0, bytes: 0 },
  );
  const blocked = hosts.filter((h) => h.verdict !== 'fetched');

  const columns: Array<DataTableColumn<ImageProxyHostRow>> = [
    { id: 'host', header: '取得元ホスト', cell: (h) => <span className="font-mono text-xs">{h.sourceHost}</span> },
    {
      id: 'verdict',
      header: '判定',
      cell: (h) => (
        <Badge variant={h.verdict === 'fetched' ? 'muted' : 'warning'} size="sm">
          {h.verdict}
        </Badge>
      ),
    },
    { id: 'fetches', header: '件数', align: 'right', cell: (h) => formatCount(h.fetches) },
    { id: 'bytes', header: '転送量', align: 'right', cell: (h) => formatBytes(h.bytes) },
  ];

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">期間</span>
        <div className="w-28">
          <NativeSelect value={days} onChange={setDays} options={USAGE_DAYS} />
        </div>
        <RefetchingDot active={query.isFetching && !query.isLoading} />
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="取得件数" value={formatCount(totals.fetches)} unit="件" />
        <StatCard label="転送量" value={formatBytes(totals.bytes)} />
        <StatCard
          label="取得しなかったホスト"
          value={formatCount(blocked.length)}
          unit="件"
          tone={blocked.length > 0 ? 'warning' : 'default'}
          hint="トラッカー判定・SSRF 判定で止めたもの"
        />
      </div>

      <Section
        title="取得元ホストの上位"
        description="転送量の多い順に最大 200 件。どの組織が開いたかは記録も表示もしません（帯域と新しい計測ホストを見つけるための集計です）。"
      >
        {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
        <DataTable
          columns={columns}
          rows={hosts}
          rowKey={(h) => `${h.sourceHost}-${h.verdict}`}
          loading={query.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="取得の記録" />}
        />
      </Section>
    </>
  );
}
